import React, { useState, useEffect, useCallback } from 'react';
import { 
    Search, Filter, ArrowLeft, CheckCircle2, XCircle, Upload, Eye, 
    FileText, CreditCard, DollarSign, Download, Truck, Edit3, Plus, 
    Save, User, Image as ImageIcon, Hash, ShieldAlert, RotateCcw, 
    Ban, AlertCircle, RefreshCw, Package, Clock, CheckCheck, Info, Calendar,
    MessageSquare, X, Loader, Store, MapPin, Phone, Mail, Shield
} from 'lucide-react';
import { MemberOrder, TrackingEvent } from '../types';

interface AdminTransactionsProps {
    orders?: MemberOrder[];
    onUpdateOrder?: (orderId: string, updates: Partial<MemberOrder>) => void;
    onNavigate: (page: string) => void;
}

// API configuration
const API_BASE_URL = process.env.NODE_ENV === 'production' 
    ? 'https://sparklinggems.shop/api'
    : 'http://localhost/sparkling-gems-backend-php/api';

// Placeholder image constant
const PLACEHOLDER_IMAGE = 'https://via.placeholder.com/300?text=No+Image';

// Auto-generated log details based on order status (NO EMOJIS)
const AUTO_LOG_DETAILS: Record<string, string[]> = {
    'Pending': [
        'Order received and awaiting payment verification',
        'Payment confirmation pending - customer will upload proof of payment',
        'Order on hold until payment is verified'
    ],
    'Paid': [
        'Payment verified by finance team - order confirmed',
        'Order has been paid and is now being processed',
        'Payment confirmation received - preparing for fulfillment'
    ],
    'Preparing': [
        'Order assigned to fulfillment team for preparation',
        'Items are being carefully packaged and inspected',
        'Quality control check in progress',
        'Security seal applied to package'
    ],
    'Shipped': [
        'Order handed over to logistics partner for delivery',
        'Package in transit to sorting facility',
        'Order Out for delivery',
        'Shipment dispatched from Sparkling Gems warehouse',
        'Tracking number assigned and updated'
    ],
    'Ready for Pickup': [
        'Order ready for pickup at Sparkling Gems Jewelry Shop',
        'Items are ready - customer notified for pickup',
        'Order available for collection at store location'
    ],
    'Picked Up': [
        'Order picked up by customer - ID verified',
        'Items collected from shop successfully',
        'Pickup confirmation received from customer'
    ],
    'Delivered': [
        'Order delivered successfully to recipient',
        'Delivery confirmation received from courier',
        'Package received by customer - signature on file',
        'Customer satisfaction follow-up scheduled'
    ],
    'Rejected': [
        'Payment verification failed - order rejected',
        'Invalid payment proof provided - action required',
        'Rejection notice sent to customer for resubmission',
        'Payment could not be verified - order on hold'
    ]
};

/**
 * Extended order type to handle API response
 */
type MemberOrderExt = MemberOrder & {
    customerName?: string;
    name?: string;
    full_name?: string;
    customer_email?: string;
    customer_phone?: string;
    shippingAddress?: string;
    rejection_reason?: string;
    rejected_at?: string;
    paymentProofUploadedAt?: string;
    payment_proof?: string;
    deliveryType?: 'shipping' | 'pickup';
    delivery_type?: 'shipping' | 'pickup';
    pickup_name?: string;
    pickup_phone?: string;
    pickup_email?: string;
    ready_for_pickup_at?: string;
    picked_up_at?: string;
    file_path?: string;
    transaction_id?: string;
    proof_file_name?: string;
    proof_uploaded_at?: string;
    proof_source?: string;
    has_proof?: boolean;
    proof_status?: string;
};

const AdminTransactions: React.FC<AdminTransactionsProps> = ({ orders = [], onUpdateOrder, onNavigate }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('All');
    const [selectedOrder, setSelectedOrder] = useState<MemberOrderExt | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [viewMode, setViewMode] = useState<'payment' | 'fulfillment' | 'info'>('payment');
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxImage, setLightboxImage] = useState('');
    const [orderTimeline, setOrderTimeline] = useState<any[]>([]);
    const [paymentProofData, setPaymentProofData] = useState<any>(null);
    const [proofLoading, setProofLoading] = useState(false);
    
    // Rejection Modal State
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [isRejecting, setIsRejecting] = useState(false);

    // PENDING CHANGES
    const [pendingStatus, setPendingStatus] = useState<string | null>(null);
    const [pendingTrackingNumber, setPendingTrackingNumber] = useState<string>('');
    const [pendingLogNote, setPendingLogNote] = useState('');
    const [pendingLogLocation, setPendingLogLocation] = useState('');
    const [pendingLogProof, setPendingLogProof] = useState<string | null>(null);
    const [pendingLogProofFile, setPendingLogProofFile] = useState<File | null>(null);
    
    // UI State for Feedback
    const [isSavingFulfillment, setIsSavingFulfillment] = useState(false);
    const [fulfillmentSaveSuccess, setFulfillmentSaveSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    // Local state for real-time updates
    const [localOrders, setLocalOrders] = useState<MemberOrderExt[]>([]);
    const [loading, setLoading] = useState(true);
    const [apiError, setApiError] = useState<string | null>(null);

    // Helper function to extract numeric ID from order ID
    const extractNumericId = (orderId: string | number): number => {
        if (typeof orderId === 'number') return orderId;
        if (typeof orderId === 'string') {
            const numericPart = orderId.replace(/[^0-9]/g, '');
            const result = parseInt(numericPart, 10);
            return isNaN(result) ? 0 : result;
        }
        return 0;
    };

    // Helper function to get image URL from timeline event
    const getTimelineImageUrl = (event: any): string => {
        if (event.image_url) {
            return event.image_url;
        }
        if (event.id && event.has_image) {
            return `${API_BASE_URL}/get-image.php?id=${event.id}`;
        }
        if (event.proof_image && !event.proof_image.startsWith('blob:')) {
            if (event.proof_image.startsWith('http')) {
                return event.proof_image;
            }
            return `${API_BASE_URL}${event.proof_image}`;
        }
        return PLACEHOLDER_IMAGE;
    };

    // Helper function to get auto-generated log description based on status (NO EMOJIS)
    const getAutoLogDescription = (status: string): string => {
        const statusKey = status === 'Preparing' ? 'Preparing' : 
                         status === 'Processing' ? 'Preparing' : status;
        
        const descriptions = AUTO_LOG_DETAILS[statusKey] || AUTO_LOG_DETAILS['Pending'];
        const randomIndex = Math.floor(Math.random() * descriptions.length);
        
        return descriptions[randomIndex];
    };

    // Update URL when component mounts
    useEffect(() => {
        window.history.pushState(null, '', '/admin/transactions');
    }, []);

    // Fetch orders on component mount
    useEffect(() => {
        fetchOrdersFromDatabase();
    }, []);

    // Safe string conversion
    const safeString = (value: any): string => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'string') return value;
        if (typeof value === 'number') return value.toString();
        if (typeof value === 'boolean') return value.toString();
        if (typeof value === 'object') {
            try {
                return JSON.stringify(value);
            } catch {
                return '';
            }
        }
        return '';
    };

    const safeLower = (v: any): string => {
        return safeString(v).toLowerCase();
    };

    const isPickupOrder = (order: MemberOrderExt | null) => {
        if (!order) return false;
        return (
            order.deliveryType === 'pickup' ||
            order.delivery_type === 'pickup' ||
            !!(order.shippingAddress && order.shippingAddress.includes('Store Pickup'))
        );
    };

    // Check if there are any pending changes
    const hasPendingChanges = (): boolean => {
        if (!selectedOrder) return false;
        const statusChanged = pendingStatus !== null && pendingStatus !== selectedOrder.status;
        const trackingChanged = pendingTrackingNumber !== (selectedOrder.trackingNumber || '');
        const logEntryAdded =
            pendingLogNote.trim() !== '' ||
            pendingLogProof !== null ||
            pendingLogLocation.trim() !== '';
        return statusChanged || trackingChanged || logEntryAdded;
    };

    // FIXED: Enhanced fetch payment proof with better error handling
    const fetchPaymentProof = useCallback(async (orderId: string | number) => {
        if (!orderId) return;
        
        setProofLoading(true);
        setPaymentProofData(null);
        
        try {
            const numericId = extractNumericId(orderId);
            if (!numericId) {
                console.error('Invalid order ID:', orderId);
                setProofLoading(false);
                return;
            }
            
            // Add timestamp to prevent caching
            const timestamp = Date.now();
            const url = `${API_BASE_URL}/orders/get-payment-proof.php?order_id=${numericId}&_=${timestamp}`;
            
            console.log(`Fetching payment proof for order ID: ${numericId}`);
            
            const response = await fetch(url, {
                headers: {
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache'
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            console.log('Payment proof API response:', data);
            
            if (data.success && data.has_proof && data.proof) {
                console.log('Proof found! Source:', data.proof_source || 'unknown');
                setPaymentProofData(data.proof);
                
                // Update the selected order with the proof data
                setSelectedOrder(prev => {
                    if (prev && prev.id === orderId) {
                        return {
                            ...prev,
                            paymentProof: data.proof.file_path,
                            payment_proof: data.proof.file_path,
                            transactionId: data.proof.transaction_id || prev.transactionId,
                            transaction_id: data.proof.transaction_id,
                            file_path: data.proof.file_path,
                            proof_file_name: data.proof.file_name,
                            proof_uploaded_at: data.proof.uploaded_at,
                            has_proof: true,
                            proof_source: data.proof_source,
                            proof_status: data.proof.status
                        } as MemberOrderExt;
                    }
                    return prev;
                });
            } else {
                console.log('No payment proof found for order:', numericId);
                setPaymentProofData(null);
            }
        } catch (error) {
            console.error('Error fetching payment proof:', error);
            setPaymentProofData(null);
        } finally {
            setProofLoading(false);
        }
    }, []);

    // Fetch timeline
    const fetchTimeline = useCallback(async (orderId: string | number) => {
        try {
            const numericId = extractNumericId(orderId);
            if (!numericId) {
                console.error('Invalid order ID:', orderId);
                return;
            }
            const timestamp = new Date().getTime();
            const response = await fetch(`${API_BASE_URL}/orders/get-timeline.php?order_id=${numericId}&_=${timestamp}`);
            const data = await response.json();
            if (data.success) {
                const sortedTimeline = (data.timeline || []).sort((a: any, b: any) => 
                    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                );
                setOrderTimeline(sortedTimeline);
            } else {
                setOrderTimeline([]);
            }
        } catch (error) {
            console.error('Error fetching timeline:', error);
            setOrderTimeline([]);
        }
    }, []);

    // Fetch payment proof and timeline when selected order changes
    useEffect(() => {
        if (!selectedOrder?.id) return;
        
        // Fetch timeline
        fetchTimeline(selectedOrder.id);
        
        // Fetch payment proof when viewing payment tab
        if (viewMode === 'payment') {
            fetchPaymentProof(selectedOrder.id);
        }
    }, [selectedOrder?.id, viewMode, fetchPaymentProof, fetchTimeline]);

    // Fetch fresh orders from API
    const fetchOrdersFromDatabase = async () => {
        setLoading(true);
        setApiError(null);
        try {
            console.log('Fetching orders from API...');
            const timestamp = Date.now();
            const response = await fetch(`${API_BASE_URL}/orders/get.php?admin=true&_=${timestamp}`, {
                headers: {
                    'Cache-Control': 'no-cache'
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            console.log('Orders API response:', data);
            
            if (data.success) {
                let ordersData: MemberOrderExt[] = [];
                
                // Handle different response formats
                if (Array.isArray(data.orders)) {
                    ordersData = data.orders;
                } else if (Array.isArray(data.data)) {
                    ordersData = data.data;
                } else if (Array.isArray(data)) {
                    ordersData = data;
                }
                
                console.log(`Found ${ordersData.length} orders`);
                
                // Process orders to check for proofs
                const processedOrders = ordersData.map(order => ({
                    ...order,
                    has_proof: !!(order.paymentProof || order.payment_proof || order.file_path || order.payment_proof),
                    proof_status: (order as any).proof_status || 'pending'
                }));
                
                setLocalOrders(processedOrders);
                
                // Update parent if callback exists
                if (onUpdateOrder && ordersData.length > 0) {
                    ordersData.forEach((order: MemberOrderExt) => {
                        onUpdateOrder(order.id, order);
                    });
                }
            } else {
                console.error('API returned error:', data.error);
                setApiError(data.error || 'Failed to fetch orders');
                setLocalOrders([]);
            }
        } catch (error) {
            console.error('Error fetching orders:', error);
            setApiError(error instanceof Error ? error.message : 'Connection error');
            setLocalOrders([]);
        } finally {
            setLoading(false);
        }
    };

    // Update order in database
    const updateOrderInDatabase = async (orderId: string, updates: Partial<MemberOrder>) => {
        try {
            const numericId = extractNumericId(orderId);
            if (!numericId) {
                throw new Error('Invalid order ID');
            }
            const response = await fetch(`${API_BASE_URL}/orders/update.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: numericId, ...updates })
            });
            const data = await response.json();
            if (data.success) {
                setLocalOrders(prev =>
                    prev.map(o => (o.id === orderId ? ({ ...o, ...updates } as MemberOrderExt) : o))
                );
                if (onUpdateOrder) {
                    onUpdateOrder(orderId, updates);
                }
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error updating order:', error);
            return false;
        }
    };

    // Handle fulfillment save with auto-generated log details (NO EMOJIS)
    const handleSaveFulfillment = async () => {
        if (!selectedOrder) return;
        
        const statusChanged = pendingStatus !== null && pendingStatus !== selectedOrder.status;
        const trackingChanged = pendingTrackingNumber !== (selectedOrder.trackingNumber || '');
        const logEntryAdded =
            pendingLogNote.trim() !== '' ||
            pendingLogProof !== null ||
            pendingLogLocation.trim() !== '';
        
        if (!statusChanged && !trackingChanged && !logEntryAdded) {
            alert('No changes to save.');
            return;
        }
        
        setIsSavingFulfillment(true);
        setError(null);
        
        try {
            const numericId = extractNumericId(selectedOrder.id);
            if (!numericId) {
                throw new Error('Invalid order ID');
            }
            
            if (statusChanged || trackingChanged) {
                const updateData: any = { id: numericId };
                if (statusChanged) updateData.status = pendingStatus;
                if (trackingChanged) updateData.tracking_number = pendingTrackingNumber;
                
                const orderResponse = await fetch(`${API_BASE_URL}/orders/update.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(updateData)
                });
                
                const orderResult = await orderResponse.json();
                
                if (!orderResult.success) {
                    throw new Error(orderResult.error || 'Failed to update order');
                }
            }
            
            if (logEntryAdded || statusChanged) {
                const formData = new FormData();
                formData.append('order_id', numericId.toString());
                
                let description = '';
                
                if (statusChanged) {
                    // Auto-generate log description based on status (NO EMOJIS)
                    description = getAutoLogDescription(pendingStatus!);
                } else if (trackingChanged) {
                    description = `Tracking number assigned: ${pendingTrackingNumber}. You can now track your order via our website or the courier's portal. Estimated delivery: 3-5 business days.`;
                } else if (pendingLogNote.trim() !== '') {
                    description = pendingLogNote.trim();
                } else {
                    description = 'Fulfillment update processed by admin.';
                }
                
                formData.append('description', description);
                formData.append('location', pendingLogLocation || 'Sparkling Gems HQ');
                
                const statusToUse = statusChanged ? pendingStatus : selectedOrder.status;
                formData.append('status', statusToUse || 'Pending');
                formData.append('created_by', 'admin');
                
                if (pendingLogProofFile) {
                    formData.append('proof_image', pendingLogProofFile);
                    console.log('Appending file:', pendingLogProofFile.name);
                } else if (pendingLogProof && pendingLogProof.startsWith('blob:')) {
                    try {
                        const response = await fetch(pendingLogProof);
                        const blob = await response.blob();
                        const file = new File([blob], 'proof.jpg', { type: blob.type || 'image/jpeg' });
                        formData.append('proof_image', file);
                        console.log('Converted blob to file');
                    } catch (err) {
                        console.error('Failed to convert blob to file:', err);
                    }
                }
                
                const timelineResponse = await fetch(`${API_BASE_URL}/orders/add-timeline.php`, {
                    method: 'POST',
                    body: formData
                });
                
                const timelineResult = await timelineResponse.json();
                console.log('Timeline response:', timelineResult);
            }
            
            setSelectedOrder(prev =>
                prev
                    ? ({
                          ...prev,
                          status: statusChanged && pendingStatus ? pendingStatus : prev.status,
                          trackingNumber: trackingChanged ? pendingTrackingNumber : prev.trackingNumber
                      } as MemberOrderExt)
                    : null
            );

            setPendingLogNote('');
            setPendingLogLocation('');
            setPendingLogProof(null);
            setPendingLogProofFile(null);
            setPendingStatus(selectedOrder.status);
            
            setFulfillmentSaveSuccess(true);
            setTimeout(() => setFulfillmentSaveSuccess(false), 3000);
            
            await fetchOrdersFromDatabase();
            
            if (selectedOrder) {
                await fetchTimeline(selectedOrder.id);
            }
            
            alert('Fulfillment updated successfully!');
        } catch (error) {
            console.error('Error saving fulfillment:', error);
            setError(error instanceof Error ? error.message : 'Failed to save fulfillment');
            alert('Error saving fulfillment. Please try again.');
        } finally {
            setIsSavingFulfillment(false);
        }
    };

    // Get customer name
    const getCustomerName = (order: MemberOrderExt): string => {
        if (isPickupOrder(order)) {
            return order.pickup_name || order.customerName || order.name || order.full_name || 'Guest';
        }
        return order.customerName ?? order.name ?? order.full_name ?? 'Guest';
    };

    // Get delivery type display
    const getDeliveryTypeDisplay = (order: MemberOrderExt) => {
        const isPickup = isPickupOrder(order);
        if (isPickup) {
            return {
                icon: <Store size={14} className="text-amber-600" />,
                text: 'Store Pickup',
                color: 'bg-amber-50 text-amber-700 border-amber-200'
            };
        }
        return {
            icon: <Truck size={14} className="text-blue-600" />,
            text: 'Shipping',
            color: 'bg-blue-50 text-blue-700 border-blue-200'
        };
    };

    // FIXED: Enhanced proof detection - checks all possible sources
    const hasProof = (order: MemberOrderExt): boolean => {
        return !!(
            order.paymentProof ||
            order.payment_proof ||
            order.file_path ||
            (order as any).payment_proof ||
            (order as any).has_proof === true ||
            (order as any).proof_uploaded_at ||
            paymentProofData?.file_path
        );
    };

    // Get payment method display
    const getPaymentMethodDisplay = (order: MemberOrderExt): { icon: JSX.Element, text: string } => {
        const proofExists = hasProof(order);
        
        if (proofExists) {
            return { 
                icon: <span className="text-gold-600 font-bold text-sm">₱</span>, 
                text: 'Bank Transfer' 
            };
        }
        
        const paymentMethod = (order.paymentMethod || '').toLowerCase();
        const isBankTransfer = 
            paymentMethod.includes('bank') || 
            paymentMethod.includes('transfer') || 
            paymentMethod === 'bank_transfer';
        
        if (isBankTransfer) {
            return { 
                icon: <span className="text-gold-600 font-bold text-sm">₱</span>, 
                text: 'Bank Transfer' 
            };
        }
        return { 
            icon: <CreditCard size={14} className="text-stone-400" />, 
            text: 'Credit Card' 
        };
    };

    // Filter orders
    const filteredOrders = localOrders.filter(order => {
        try {
            const customerName = getCustomerName(order);
            const search = safeLower(searchTerm);
            const orderId = safeLower(order?.order_number || order?.id);
            const custName = safeLower(customerName);
            const transId = order?.transactionId ? safeLower(order.transactionId) : '';
            const matchesSearch =
                orderId.includes(search) ||
                custName.includes(search) ||
                transId.includes(search);
            const matchesFilter =
                filterStatus === 'All' || safeLower(order?.status) === safeLower(filterStatus);
            return matchesSearch && matchesFilter;
        } catch {
            return false;
        }
    });

    // Count pending proofs
    const pendingProofsCount = localOrders.filter(o => o.status === 'Pending' && !hasProof(o)).length;

    // Lightbox controls
    const openLightbox = (imageUrl: string, e?: React.MouseEvent) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setLightboxImage(imageUrl);
        setLightboxOpen(true);
    };

    const closeLightbox = () => {
        setLightboxOpen(false);
        setLightboxImage('');
    };

    // Open manage modal
    const openManageModal = (order: MemberOrderExt) => {
        setSelectedOrder(order);
        setViewMode('info');
        setPendingStatus(order.status);
        setPendingTrackingNumber(order.trackingNumber || '');
        setPendingLogNote('');
        setPendingLogLocation('');
        setPendingLogProof(null);
        setPendingLogProofFile(null);
        setRejectionReason('');
        setError(null);
        setPaymentProofData(null);
        setOrderTimeline([]);
    };

    // Handle payment approval with auto-generated description (NO EMOJIS)
    const handleApprovePayment = async () => {
        if (!selectedOrder) return;
        setIsSavingFulfillment(true);
        setError(null);
        try {
            const numericId = extractNumericId(selectedOrder.id);
            if (!numericId) {
                throw new Error('Invalid order ID');
            }
            
            const updateData = { id: numericId, status: 'Paid' };
            const response = await fetch(`${API_BASE_URL}/orders/update.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updateData)
            });
            const data = await response.json();
            
            if (data.success) {
                // Add auto-generated timeline entry
                const formData = new FormData();
                formData.append('order_id', numericId.toString());
                formData.append('status', 'Paid');
                formData.append('description', getAutoLogDescription('Paid'));
                formData.append('created_by', 'admin');
                await fetch(`${API_BASE_URL}/orders/add-timeline.php`, {
                    method: 'POST',
                    body: formData
                }).catch(err => console.error('Timeline error:', err));
                
                setSelectedOrder(prev => prev ? { ...prev, status: 'Paid' } as MemberOrderExt : null);
                await fetchOrdersFromDatabase();
                if (selectedOrder) {
                    await fetchTimeline(selectedOrder.id);
                }
                setSelectedOrder(null);
                alert('Payment confirmed successfully!');
            } else {
                throw new Error(data.error || 'Failed to confirm payment');
            }
        } catch (error) {
            console.error('Error approving payment:', error);
            setError(error instanceof Error ? error.message : 'Failed to confirm payment');
            alert('Error approving payment. Please try again.');
        } finally {
            setIsSavingFulfillment(false);
        }
    };

    // Handle payment rejection with auto-generated description (NO EMOJIS)
    const handleRejectWithReason = async () => {
        if (!selectedOrder || !rejectionReason.trim()) return;
        setIsRejecting(true);
        try {
            const numericId = extractNumericId(selectedOrder.id);
            if (!numericId) {
                throw new Error('Invalid order ID');
            }
            const updates: any = { 
                status: 'Rejected',
                rejection_reason: rejectionReason
            };
            const response = await fetch(`${API_BASE_URL}/orders/update.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: numericId, ...updates })
            });
            const data = await response.json();
            
            if (data.success) {
                const formData = new FormData();
                formData.append('order_id', numericId.toString());
                formData.append('status', 'Rejected');
                formData.append('description', `${getAutoLogDescription('Rejected')} Reason: ${rejectionReason}`);
                formData.append('created_by', 'admin');
                await fetch(`${API_BASE_URL}/orders/add-timeline.php`, {
                    method: 'POST',
                    body: formData
                });
                setShowRejectModal(false);
                setSelectedOrder(null);
                await fetchOrdersFromDatabase();
                alert('Payment rejected successfully.');
            } else {
                throw new Error(data.error || 'Failed to reject payment');
            }
        } catch (error) {
            console.error('Error rejecting payment:', error);
            alert('Error rejecting payment. Please try again.');
        } finally {
            setIsRejecting(false);
        }
    };

    // Handle file upload
    const handleTrackingFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const maxSize = 10 * 1024 * 1024;
            if (file.size > maxSize) {
                alert(`File size too large. Maximum ${maxSize / (1024 * 1024)}MB allowed.`);
                return;
            }
            const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                alert('Invalid file type. Please upload JPG, PNG, GIF, or WEBP.');
                return;
            }
            const url = URL.createObjectURL(file);
            setPendingLogProof(url);
            setPendingLogProofFile(file);
            console.log('File selected:', file.name, file.type, (file.size / 1024).toFixed(2), 'KB');
            e.target.value = '';
        }
    };

    // Tab change handler
    const handleTabChange = (tab: 'payment' | 'fulfillment' | 'info') => {
        setViewMode(tab);
        if (tab === 'payment' && selectedOrder) {
            fetchPaymentProof(selectedOrder.id);
        }
    };

    // Input handlers
    const handleInputClick = (e: React.MouseEvent) => e.stopPropagation();
    const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setPendingStatus(e.target.value);
        // Auto-generate log note based on selected status (NO EMOJIS)
        if (e.target.value) {
            setPendingLogNote(getAutoLogDescription(e.target.value));
        }
    };
    const handleTrackingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPendingTrackingNumber(e.target.value);
    };
    const handleLogNoteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPendingLogNote(e.target.value);
    };
    const handleLogLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPendingLogLocation(e.target.value);
    };
    const handleModalContentClick = (e: React.MouseEvent) => e.stopPropagation();

    // FIXED: Get the payment proof image URL with better handling
    const getPaymentProofImage = (): string | null => {
        if (selectedOrder?.paymentProof) {
            return selectedOrder.paymentProof;
        }
        if (selectedOrder?.payment_proof) {
            return selectedOrder.payment_proof;
        }
        if (selectedOrder?.file_path) {
            return selectedOrder.file_path;
        }
        if (paymentProofData?.file_path) {
            return paymentProofData.file_path;
        }
        return null;
    };

    // Loading state
    if (loading) {
        return (
            <div className="pt-44 pb-12 min-h-screen bg-stone-50">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex justify-center items-center h-64">
                        <div className="text-center">
                            <Loader size={32} className="animate-spin text-gold-600 mx-auto mb-4" />
                            <p className="text-stone-500">Loading orders...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="pt-44 pb-12 min-h-screen bg-stone-50">
            <div className="max-w-7xl mx-auto px-6">
                
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
                    <div>
                        <button
                            onClick={() => onNavigate('admin-dashboard')}
                            className="flex items-center gap-2 text-stone-500 hover:text-stone-900 text-xs uppercase tracking-widest mb-2 transition-colors"
                        >
                            <ArrowLeft size={14} /> Back to Dashboard
                        </button>
                        <h1 className="font-serif text-3xl text-stone-900">Order Management</h1>
                        <p className="text-stone-500 font-light mt-2 text-sm">
                            Control order flow, verify payments, and manage shipment tracking.
                        </p>
                    </div>
                    
                    <div className="flex items-center gap-3">
                        <button
                            onClick={fetchOrdersFromDatabase}
                            className="px-4 py-2 border border-stone-200 text-stone-600 text-xs uppercase tracking-widest hover:bg-stone-50 transition-colors rounded-sm flex items-center gap-2"
                        >
                            <RefreshCw size={14} /> Refresh
                        </button>
                        {pendingProofsCount > 0 && (
                            <div className="bg-orange-100 text-orange-700 px-3 py-2 rounded-sm text-xs font-bold flex items-center gap-2">
                                <AlertCircle size={14} /> {pendingProofsCount} pending order{pendingProofsCount !== 1 ? 's' : ''} waiting for proof
                            </div>
                        )}
                    </div>
                </div>

                {/* API Error Message */}
                {apiError && (
                    <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm flex items-center gap-2">
                        <AlertCircle size={16} />
                        <span className="text-sm">{apiError}</span>
                        <button onClick={() => fetchOrdersFromDatabase()} className="ml-auto text-red-600 hover:text-red-800 underline text-sm">
                            Retry
                        </button>
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm flex items-center gap-2">
                        <AlertCircle size={16} />
                        <span className="text-sm">{error}</span>
                        <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
                            <X size={16} />
                        </button>
                    </div>
                )}

                {/* Orders Table */}
                <div className="bg-white border border-stone-200 shadow-sm rounded-sm overflow-hidden">
                    <div className="p-6 border-b border-stone-100 flex flex-col md:flex-row gap-4 justify-between bg-stone-50/30">
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                            <input 
                                type="text" 
                                placeholder="Search Order ID or Customer..." 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-stone-200 rounded-sm focus:border-gold-500 focus:outline-none text-sm bg-white"
                            />
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                                <Filter size={16} className="text-stone-400" />
                                <select 
                                    value={filterStatus}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                    className="border border-stone-200 rounded-sm px-3 py-2 text-sm focus:border-gold-500 focus:outline-none bg-white cursor-pointer"
                                >
                                    <option value="All">All Transactions</option>
                                    <option value="Pending">Pending</option>
                                    <option value="Paid">Paid</option>
                                    <option value="Preparing">Preparing</option>
                                    <option value="Shipped">Shipped</option>
                                    <option value="Ready for Pickup">Ready for Pickup</option>
                                    <option value="Picked Up">Picked Up</option>
                                    <option value="Delivered">Delivered</option>
                                    <option value="Rejected">Rejected</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-stone-100 text-stone-500 text-[10px] uppercase tracking-widest font-bold">
                                <tr>
                                    <th className="px-6 py-4">Order Details</th>
                                    <th className="px-6 py-4">Delivery</th>
                                    <th className="px-6 py-4">Payment Method</th>
                                    <th className="px-6 py-4">Amount</th>
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4">Proof</th>
                                    <th className="px-6 py-4 text-right">Actions</th>
                                 </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                                {filteredOrders.length > 0 ? (
                                    filteredOrders.map((order) => {
                                        const customerName = getCustomerName(order);
                                        const paymentInfo = getPaymentMethodDisplay(order);
                                        const deliveryInfo = getDeliveryTypeDisplay(order);
                                        const isRejected = order.status === 'Rejected';
                                        const hasProofValue = hasProof(order);
                                        const isPickup = isPickupOrder(order);
                                        
                                        // Normalize status display
                                        const displayStatus = order.status === 'Preparing' ? 'Preparing' : order.status;
                                        
                                        return (
                                            <tr key={order.id} className={`hover:bg-stone-50/50 transition-colors ${isRejected ? 'bg-red-50/30' : ''}`}>
                                                <td className="px-6 py-4">
                                                    <div className="flex flex-col">
                                                        <span className="font-bold text-sm">{customerName}</span>
                                                        <span className="text-xs text-stone-500">Order #{order.order_number || order.id}</span>
                                                        <span className="text-[10px] text-stone-400">{order.date || order.orderDate}</span>
                                                        {isPickup && order.pickup_name && (
                                                            <span className="text-[9px] text-amber-600 mt-1">Pickup: {order.pickup_name}</span>
                                                        )}
                                                        {(order as any).rejection_reason && (
                                                            <span className="text-[9px] text-red-600 bg-red-50 px-1 py-0.5 rounded inline-flex items-center gap-1 w-fit mt-1">
                                                                <MessageSquare size={8} /> Rejected: {(order as any).rejection_reason.substring(0, 30)}...
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-2">
                                                        {deliveryInfo.icon}
                                                        <span className="text-xs">{deliveryInfo.text}</span>
                                                    </div>
                                                    {isPickup && (
                                                        <div className="text-[9px] text-amber-600 mt-1">
                                                            {order.pickup_name && `Pickup by: ${order.pickup_name}`}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex flex-col gap-1">
                                                        <div className="flex items-center gap-2 text-xs font-medium">
                                                            {paymentInfo.icon}
                                                            {paymentInfo.text}
                                                        </div>
                                                        {order.transactionId && (
                                                            <span className="text-[10px] font-mono text-stone-400">Ref: {order.transactionId}</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 font-serif">
                                                    ₱ {order.total?.toLocaleString() || '0'}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`inline-block px-2 py-1 rounded-full text-[10px] uppercase font-bold tracking-wide ${
                                                        displayStatus === 'Paid' ? 'bg-green-100 text-green-700' :
                                                        displayStatus === 'Pending' ? 'bg-orange-100 text-orange-700' :
                                                        displayStatus === 'Preparing' ? 'bg-purple-100 text-purple-700' :
                                                        displayStatus === 'Shipped' ? 'bg-blue-100 text-blue-700' :
                                                        displayStatus === 'Ready for Pickup' ? 'bg-amber-100 text-amber-700' :
                                                        displayStatus === 'Picked Up' ? 'bg-emerald-100 text-emerald-700' :
                                                        displayStatus === 'Delivered' ? 'bg-emerald-100 text-emerald-700' :
                                                        displayStatus === 'Rejected' ? 'bg-red-100 text-red-700' :
                                                        'bg-stone-100 text-stone-500'
                                                    }`}>
                                                        {displayStatus}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    {hasProofValue ? (
                                                        <button
                                                            onClick={() => {
                                                                setSelectedOrder(order);
                                                                setViewMode('payment');
                                                                fetchPaymentProof(order.id);
                                                            }}
                                                            className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1"
                                                        >
                                                            <Eye size={12} /> View Proof
                                                        </button>
                                                    ) : (
                                                        <span className="text-xs text-stone-400">No proof</span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <button 
                                                        onClick={() => openManageModal(order)}
                                                        className="px-3 py-1.5 text-xs uppercase tracking-wider transition-colors rounded-sm flex items-center gap-2 ml-auto shadow-sm bg-stone-900 text-white hover:bg-gold-600"
                                                    >
                                                        <Info size={12} /> View
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-12 text-center text-stone-500">
                                            {apiError ? 'Unable to load orders. Please refresh.' : 'No orders found'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Lightbox Modal */}
                {lightboxOpen && (
                    <div className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center" onClick={closeLightbox}>
                        <button onClick={closeLightbox} className="absolute top-5 right-5 text-white hover:text-gold-600 z-10 bg-black/50 p-2 rounded-full">
                            <XCircle size={30} />
                        </button>
                        <img
                            src={lightboxImage}
                            alt="Full size view"
                            className="max-w-[90vw] max-h-[90vh] object-contain"
                            onClick={(e) => e.stopPropagation()}
                            onError={(e) => {
                                (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
                            }}
                        />
                    </div>
                )}

                {/* Reject Modal */}
                {showRejectModal && (
                    <div className="fixed inset-0 z-[150] flex items-center justify-center">
                        <div className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm" onClick={() => setShowRejectModal(false)}></div>
                        <div className="relative bg-white w-full max-w-md rounded-sm shadow-2xl p-6 animate-fade-up-enter mx-4">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="font-serif text-xl">Reject Payment</h3>
                                <button onClick={() => setShowRejectModal(false)} className="text-stone-400 hover:text-stone-900">
                                    <X size={20} />
                                </button>
                            </div>
                            <p className="text-xs text-stone-500 mb-4">Please provide a reason for rejecting this payment.</p>
                            <textarea
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                rows={4}
                                className="w-full border border-stone-300 p-3 text-sm rounded-sm focus:border-gold-500 outline-none mb-4 resize-none"
                                placeholder="e.g., Proof of payment is unclear, Incorrect amount..."
                                autoFocus
                            />
                            <div className="flex gap-3 justify-end">
                                <button
                                    onClick={() => setShowRejectModal(false)}
                                    className="text-xs text-stone-500 hover:text-stone-900 px-4 py-2"
                                    disabled={isRejecting}
                                >
                                    Cancel
                                </button>
                                <button 
                                    onClick={handleRejectWithReason}
                                    disabled={!rejectionReason.trim() || isRejecting}
                                    className="bg-red-600 text-white px-6 py-2 text-xs uppercase tracking-widest hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                                >
                                    {isRejecting ? <><Loader size={14} className="animate-spin" /> Rejecting...</> : 'Confirm Rejection'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Order Details Modal */}
                {selectedOrder && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center">
                        <div className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm" onClick={() => setSelectedOrder(null)}></div>
                        
                        <div
                            className="relative bg-white w-full max-w-3xl h-[90vh] overflow-y-auto rounded-sm shadow-2xl animate-fade-up-enter mx-4 flex flex-col"
                            onClick={handleModalContentClick}
                        >
                            {/* Modal Header */}
                            <div className="p-6 border-b border-stone-100 flex justify-between items-center bg-stone-50 sticky top-0 z-10">
                                <div>
                                    <h3 className="font-serif text-2xl">Order #{selectedOrder.order_number || selectedOrder.id}</h3>
                                    <p className="text-xs text-stone-500">{getCustomerName(selectedOrder)} • {selectedOrder.date || selectedOrder.orderDate}</p>
                                    {isPickupOrder(selectedOrder) && (
                                        <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 bg-amber-50 border border-amber-200 rounded-sm">
                                            <Store size={12} className="text-amber-600" />
                                            <span className="text-[10px] text-amber-700 font-medium">Store Pickup</span>
                                        </div>
                                    )}
                                </div>
                                <button onClick={() => setSelectedOrder(null)} className="text-stone-400 hover:text-stone-900">
                                    <XCircle size={24} />
                                </button>
                            </div>

                            {/* Tabs */}
                            <div className="flex border-b border-stone-200">
                                <button
                                    onClick={() => handleTabChange('info')}
                                    className={`flex-1 py-4 text-xs uppercase tracking-widest font-bold ${viewMode === 'info' ? 'border-b-2 border-gold-600 text-gold-600 bg-white' : 'text-stone-500 bg-stone-50 hover:bg-stone-100'}`}
                                >
                                    Customer Info
                                </button>
                                <button
                                    onClick={() => handleTabChange('payment')}
                                    className={`flex-1 py-4 text-xs uppercase tracking-widest font-bold ${viewMode === 'payment' ? 'border-b-2 border-gold-600 text-gold-600 bg-white' : 'text-stone-500 bg-stone-50 hover:bg-stone-100'}`}
                                >
                                    Payment {proofLoading && <Loader size={12} className="inline ml-1 animate-spin" />}
                                </button>
                                <button
                                    onClick={() => handleTabChange('fulfillment')}
                                    className={`flex-1 py-4 text-xs uppercase tracking-widest font-bold ${viewMode === 'fulfillment' ? 'border-b-2 border-gold-600 text-gold-600 bg-white' : 'text-stone-500 bg-stone-50 hover:bg-stone-100'}`}
                                >
                                    Fulfillment
                                </button>
                            </div>

                            {/* Tab Content */}
                            <div className="p-8 flex-1 overflow-y-auto" onClick={handleModalContentClick}>
                                {/* Customer Info Tab */}
                                {viewMode === 'info' && (
                                    <div className="space-y-6">
                                        <div className="bg-stone-50 p-6 rounded-sm border border-stone-200">
                                            <h4 className="text-xs uppercase tracking-widest text-stone-500 font-bold mb-4 flex items-center gap-2">
                                                <User size={14} /> Customer Information
                                            </h4>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <p className="text-[10px] text-stone-400">Name</p>
                                                    <p className="text-sm font-medium">{getCustomerName(selectedOrder)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-[10px] text-stone-400">Email</p>
                                                    <p className="text-sm">{selectedOrder.customer_email || selectedOrder.pickup_email || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-[10px] text-stone-400">Phone</p>
                                                    <p className="text-sm">{selectedOrder.customer_phone || selectedOrder.pickup_phone || 'N/A'}</p>
                                                </div>
                                                <div className="col-span-2">
                                                    <p className="text-[10px] text-stone-400">
                                                        {isPickupOrder(selectedOrder) ? 'Pickup Location' : 'Shipping Address'}
                                                    </p>
                                                    <p className="text-sm bg-white p-3 rounded-sm border border-stone-200 mt-1">
                                                        {isPickupOrder(selectedOrder)
                                                            ? 'Sparkling Gems Jewelry Shop - 2F, San Antonio Building, Corumi, Quezon City'
                                                            : (selectedOrder.shippingAddress || 'No shipping address provided')}
                                                    </p>
                                                </div>
                                                {isPickupOrder(selectedOrder) && (
                                                    <div className="col-span-2 bg-amber-50 p-3 rounded-sm border border-amber-200">
                                                        <p className="text-xs text-amber-800">
                                                            <span className="font-bold">Pickup Person:</span> {selectedOrder.pickup_name}<br />
                                                            <span className="font-bold">Contact:</span> {selectedOrder.pickup_phone}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="bg-stone-50 p-6 rounded-sm border border-stone-200">
                                            <h4 className="text-xs uppercase tracking-widest text-stone-500 font-bold mb-4 flex items-center gap-2">
                                                <Package size={14} /> Order Items
                                            </h4>
                                            <div className="space-y-3">
                                                {selectedOrder.items?.map((item, idx) => (
                                                    <div key={idx} className="flex items-center gap-3 p-2 bg-white rounded-sm">
                                                        <div className="w-12 h-12 bg-stone-100 rounded-sm overflow-hidden">
                                                            {item.image ? (
                                                                <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                                                            ) : (
                                                                <ImageIcon size={16} className="text-stone-300 m-auto" />
                                                            )}
                                                        </div>
                                                        <div className="flex-1">
                                                            <p className="text-sm font-medium">{item.name}</p>
                                                            <p className="text-xs text-stone-500">Qty: {item.quantity} • ₱{item.price?.toLocaleString()}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Payment Tab */}
                                {viewMode === 'payment' && (
                                    <div className="space-y-6">
                                        <div className={`p-4 rounded-sm border ${
                                            selectedOrder.status === 'Paid' ? 'bg-green-50 border-green-200' : 
                                            selectedOrder.status === 'Rejected' ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200'
                                        }`}>
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className="text-gold-600 font-bold text-lg">₱</span>
                                                <span className={`text-sm font-bold ${
                                                    selectedOrder.status === 'Paid' ? 'text-green-800' :
                                                    selectedOrder.status === 'Rejected' ? 'text-red-800' : 'text-orange-800'
                                                }`}>
                                                    Status: {selectedOrder.status}
                                                </span>
                                            </div>
                                            <p className="text-xs">Total: ₱ {selectedOrder.total?.toLocaleString() || '0'}</p>
                                            <p className="text-xs mt-1">Method: {getPaymentMethodDisplay(selectedOrder).text}</p>
                                            {selectedOrder.transactionId && <p className="text-xs mt-1">Ref: {selectedOrder.transactionId}</p>}
                                            {selectedOrder.proof_uploaded_at && (
                                                <p className="text-xs text-stone-500 mt-1">Uploaded: {new Date(selectedOrder.proof_uploaded_at).toLocaleString()}</p>
                                            )}
                                            {paymentProofData?.proof_source && (
                                                <p className="text-xs text-stone-400 mt-1">Source: {paymentProofData.proof_source}</p>
                                            )}
                                        </div>

                                        <div>
                                            <h4 className="text-xs uppercase tracking-widest text-stone-500 font-bold mb-4">Proof of Payment</h4>
                                            {proofLoading ? (
                                                <div className="flex justify-center py-12">
                                                    <Loader size={32} className="animate-spin text-gold-600" />
                                                    <p className="text-sm text-stone-500 ml-2">Loading proof...</p>
                                                </div>
                                            ) : (() => {
                                                const proofImage = getPaymentProofImage();
                                                if (proofImage) {
                                                    return (
                                                        <div className="border border-stone-200 p-4 rounded-sm bg-stone-50">
                                                            <img
                                                                src={proofImage}
                                                                alt="Payment Proof"
                                                                className="max-h-64 object-contain cursor-pointer mx-auto"
                                                                onClick={() => openLightbox(proofImage)}
                                                                onError={(e) => {
                                                                    console.error('Failed to load proof image');
                                                                    (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
                                                                }}
                                                            />
                                                            <p className="text-xs text-center text-stone-500 mt-2">
                                                                {selectedOrder.proof_file_name && `File: ${selectedOrder.proof_file_name}`}
                                                            </p>
                                                            {paymentProofData?.status && (
                                                                <p className="text-xs text-center text-stone-400 mt-1">
                                                                    Status: {paymentProofData.status}
                                                                </p>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                return (
                                                    <div className="h-40 border-2 border-dashed border-stone-300 rounded-sm flex items-center justify-center text-stone-400">
                                                        <FileText size={32} />
                                                        <p className="text-sm ml-2">No proof uploaded</p>
                                                    </div>
                                                );
                                            })()}
                                        </div>

                                        {selectedOrder.status === 'Pending' && getPaymentProofImage() && (
                                            <div className="flex gap-4 pt-6">
                                                <button
                                                    onClick={handleApprovePayment}
                                                    className="flex-1 bg-stone-900 text-white py-3 uppercase text-xs hover:bg-gold-600 transition-colors rounded-sm"
                                                >
                                                    Confirm Payment
                                                </button>
                                                <button
                                                    onClick={() => setShowRejectModal(true)}
                                                    className="flex-1 border border-red-200 text-red-500 py-3 uppercase text-xs hover:bg-red-50 transition-colors rounded-sm"
                                                >
                                                    Reject
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Fulfillment Tab */}
                                {viewMode === 'fulfillment' && (
                                    <div className="space-y-8">
                                        <div className="flex justify-end">
                                            <button
                                                onClick={handleSaveFulfillment}
                                                disabled={isSavingFulfillment || !hasPendingChanges()}
                                                className={`px-6 py-3 text-white text-xs uppercase tracking-widest rounded-sm flex items-center gap-2 shadow-lg ${
                                                    hasPendingChanges() ? 'bg-gold-600 hover:bg-gold-700' : 'bg-stone-400 cursor-not-allowed'
                                                }`}
                                            >
                                                {isSavingFulfillment ? <><Loader size={16} className="animate-spin" /> Saving...</> : <><Save size={16} /> Save Changes</>}
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-xs uppercase tracking-widest font-bold mb-2">Order Status</label>
                                                <select 
                                                    value={pendingStatus ?? selectedOrder.status} 
                                                    onChange={handleStatusChange}
                                                    className="w-full border border-stone-300 p-2 rounded-sm text-sm bg-white"
                                                >
                                                    <option value="Pending">Pending</option>
                                                    <option value="Paid">Paid</option>
                                                    <option value="Preparing">Preparing</option>
                                                    <option value="Shipped">Shipped</option>
                                                    <option value="Ready for Pickup">Ready for Pickup</option>
                                                    <option value="Picked Up">Picked Up</option>
                                                    <option value="Delivered">Delivered</option>
                                                    <option value="Rejected">Rejected</option>
                                                </select>
                                                <p className="text-[10px] text-stone-400 mt-1">
                                                    {pendingStatus && `Auto-log: ${getAutoLogDescription(pendingStatus).substring(0, 60)}...`}
                                                </p>
                                            </div>
                                            <div>
                                                <label className="block text-xs uppercase tracking-widest font-bold mb-2">Tracking Number</label>
                                                <input 
                                                    type="text" 
                                                    value={pendingTrackingNumber} 
                                                    onChange={handleTrackingChange}
                                                    placeholder={isPickupOrder(selectedOrder) ? 'N/A for pickup' : 'LBC-12345678'}
                                                    disabled={isPickupOrder(selectedOrder)}
                                                    className={`w-full border border-stone-300 p-2 rounded-sm text-sm bg-white ${isPickupOrder(selectedOrder) ? 'opacity-50' : ''}`} 
                                                />
                                            </div>
                                        </div>

                                        <div className="border-t border-stone-100 pt-6">
                                            <h4 className="text-xs uppercase tracking-widest font-bold mb-4">Add Timeline Entry</h4>
                                            <div className="bg-stone-50 p-4 rounded-sm border border-stone-200">
                                                <div className="grid grid-cols-3 gap-4 mb-4">
                                                    <input 
                                                        type="text" 
                                                        placeholder="Log Details (auto-filled when status changes)" 
                                                        value={pendingLogNote} 
                                                        onChange={handleLogNoteChange}
                                                        className="col-span-2 border border-stone-300 p-2 text-sm rounded-sm bg-white" 
                                                    />
                                                    <input 
                                                        type="text" 
                                                        placeholder="Location" 
                                                        value={pendingLogLocation} 
                                                        onChange={handleLogLocationChange}
                                                        className="border border-stone-300 p-2 text-sm rounded-sm bg-white" 
                                                    />
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <label className="cursor-pointer bg-stone-100 hover:bg-stone-200 px-4 py-2 rounded-sm text-xs transition-colors flex items-center gap-2">
                                                        <Upload size={12} />
                                                        Upload Image
                                                        <input 
                                                            type="file" 
                                                            className="hidden" 
                                                            accept="image/*"
                                                            onChange={handleTrackingFileUpload}
                                                        />
                                                    </label>
                                                    {pendingLogProof && (
                                                        <div className="relative w-16 h-16 border border-stone-200 rounded-sm overflow-hidden">
                                                            <img src={pendingLogProof} alt="Preview" className="w-full h-full object-cover" />
                                                            <button
                                                                onClick={() => {
                                                                    setPendingLogProof(null);
                                                                    setPendingLogProofFile(null);
                                                                }}
                                                                className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5"
                                                            >
                                                                <X size={12} />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <h4 className="text-xs uppercase tracking-widest font-bold mb-4">Timeline History</h4>
                                            <div className="space-y-4 max-h-60 overflow-y-auto">
                                                {orderTimeline.length > 0 ? orderTimeline.map((event, idx) => (
                                                    <div key={idx} className="flex gap-4 text-sm border-l-2 border-gold-400 pl-4 relative">
                                                        <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-gold-600 border-2 border-white"></div>
                                                        <div className="text-xs text-stone-400 w-24">
                                                            {new Date(event.created_at).toLocaleDateString()}<br />
                                                            <span className="text-[9px]">{new Date(event.created_at).toLocaleTimeString()}</span>
                                                        </div>
                                                        <div className="flex-1">
                                                            <p className="font-bold">
                                                                {event.status === 'Processing' ? 'Preparing' : (event.status || 'Update')}
                                                            </p>
                                                            <p className="text-stone-600">{event.description || event.notes}</p>
                                                            {event.location && event.location !== 'Admin Portal' && event.location !== 'admin' && (
                                                                <p className="text-xs text-stone-400">Location: {event.location}</p>
                                                            )}
                                                            {event.proof_image && (
                                                                <div className="mt-2 w-20 h-20 border border-stone-200 rounded-sm overflow-hidden cursor-pointer"
                                                                     onClick={() => openLightbox(getTimelineImageUrl(event))}>
                                                                    <img 
                                                                        src={getTimelineImageUrl(event)} 
                                                                        alt="Proof" 
                                                                        className="w-full h-full object-cover"
                                                                        onError={(e) => {
                                                                            (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
                                                                        }}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )) : <p className="text-sm text-stone-400 italic text-center">No timeline entries yet. Add a timeline entry to track this order.</p>}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminTransactions;